import { ContactForm } from "./ContactForm";
import { Check, Clock, Shield, Users, Star, Calendar } from "lucide-react";
import heroImage from "@/assets/hero-roofing.jpg";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

interface HeroProps {
  cityName?: string;
}

export const Hero = ({ cityName = "Long Island" }: HeroProps) => {
  const [county, setCounty] = useState<string>("Long Island");

  useEffect(() => {
    const fetchCounty = async () => {
      if (!cityName || cityName === "Long Island") {
        setCounty("Long Island");
        return;
      }

      try {
        const { data, error } = await supabase
          .from('city_groups')
          .select('county')
          .or(`primary_city.ilike.${cityName},display_cities.cs.{${cityName}}`)
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching county:', error);
          setCounty("Long Island");
        } else if (data?.county) {
          setCounty(data.county);
        } else {
          setCounty("Long Island");
        }
      } catch (error) {
        console.error('Error fetching county:', error);
        setCounty("Long Island");
      }
    };

    fetchCounty();
  }, [cityName]);
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${heroImage})` }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/85 to-blue-700/90" />
      
      <div className="relative container mx-auto px-4 py-12 lg:py-16 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Side - Content */}
          <div className="text-white space-y-6">
            <div className="space-y-2">
              <p className="text-xs font-medium text-blue-100 tracking-wide uppercase">
                Affordable. Reliable. Fully Insured.
              </p>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                #1 Roofing Experts in{" "}
                <span className="text-blue-200">{cityName}</span>
              </h1>
              <p className="text-lg text-blue-100 leading-relaxed max-w-lg">
                Fast, Reliable Roof Repairs & Replacements in {cityName}
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">Licensed & Insured</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">Serving {county === "Long Island" ? county : `${county} County`} Since 2007</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="font-semibold text-white">24/7 Emergency Service</p>
                </div>
              </div>
            </div>


            {/* CTA Button */}
            <div className="pt-4">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/90 font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-200"
                asChild
              >
                <a href="/book-appointment">
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Free Estimate
                </a>
              </Button>
            </div>

            {/* Hero Testimonial */}
            <div className="pt-4">
              <div className="bg-blue-800/30 border border-blue-600/30 rounded-lg p-6 backdrop-blur-sm">
                {/* 5 Star Rating */}
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                {/* Testimonial Quote */}
                <p className="text-blue-100 text-lg italic mb-3">
                  "Safeway fixed my leaking roof the same day. Professional and fast!"
                </p>
                <p className="text-blue-200 font-medium mb-4">
                  – John D., {cityName}
                </p>
                
                {/* Reviews Link */}
                <button
                  onClick={() => {
                    const testimonialsSection = document.getElementById('testimonials');
                    testimonialsSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-blue-200 hover:text-white font-medium underline underline-offset-4 hover:underline-offset-8 transition-all duration-300"
                >
                  View All Reviews →
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="flex justify-center lg:justify-end">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
};